import { lazy, useEffect } from 'react';
import { useNavigate, type RouteObject } from 'react-router';
import { RequireRole } from '@/lib/auth/guards';
import { usePrincipal } from '@/lib/auth/use-auth';
import { Skeleton, toast } from '@/lib/ui';
import { createDraftAgent } from './api';
import { useAiProviders } from './use-ai-settings';

const AgentDetailPage = lazy(() => import('./detail-page').then((m) => ({ default: m.AgentDetailPage })));
const AgentRosterPage = lazy(() => import('./roster-page').then((m) => ({ default: m.AgentRosterPage })));
const WizardPage = lazy(() => import('./wizard/wizard-page').then((m) => ({ default: m.WizardPage })));

/** /agents/onboard creates a Draft and jumps into its wizard, so the nav link always lands somewhere useful. */
function OnboardRedirect() {
  const navigate = useNavigate();
  const me = usePrincipal();
  const { providers, enabled, loading } = useAiProviders();
  useEffect(() => {
    if (loading) return;
    const provider = enabled[0] ?? 'openai';
    createDraftAgent(me, { provider, model: providers[provider].defaultModel })
      .then((id) => navigate(`/agents/onboard/${id}`, { replace: true }))
      .catch((e) => { toast.error(e instanceof Error ? e.message : 'Could not create a draft'); navigate('/agents', { replace: true }); });
  }, [loading, enabled, providers, me, navigate]);
  return <Skeleton lines={3} />;
}

export const agentRoutes: RouteObject[] = [
  {
    path: 'agents',
    handle: { crumb: 'AI Agents' },
    children: [
      { index: true, element: <AgentRosterPage /> },
      {
        element: <RequireRole roles={['admin']} />,
        children: [
          { path: 'onboard', element: <OnboardRedirect />, handle: { crumb: 'Onboard' } },
          { path: 'onboard/:id', element: <WizardPage />, handle: { crumb: (p: Record<string, string | undefined>) => `Onboard ${p.id ?? ''}` } },
        ],
      },
      { path: ':id', element: <AgentDetailPage />, handle: { crumb: (p: Record<string, string | undefined>) => p.id ?? 'Agent' } },
    ],
  },
];
